import { setDefaultRequirementStatuses, getCompletedLevels } from './progressUtils'
import { shouldExcludeRequirement, resolveRequirementStatus } from './requirementStatusResolver'

const CURRICULUM_PREFIXES = {
  3.1: {
    Associate: 'F',
    Student: 'V',
  },
  '3.0': {
    Associate: 'A',
    Student: 'S',
    Reciprocity: 'Z',
  },
  '2.0': {
    Associate: 'R',
    Student: 'U',
  },
  '1.0': {
    Associate: 'R',
    Student: 'R',
  },
}

const CAREER_PATH_SUFFIXES = {
  operations: 'O',
  teaching: 'T',
  coaching: 'T',
  executive: 'E',
  management: 'E',
}

const getCurriculumPrefix = (version, programType) => {
  return CURRICULUM_PREFIXES[version]?.[programType] || null
}

const getCareerPathSuffix = (careerPath) => {
  if (!careerPath) return null

  const pathLower = careerPath.toLowerCase()

  for (const [keyword, suffix] of Object.entries(CAREER_PATH_SUFFIXES)) {
    if (pathLower.includes(keyword)) {
      return suffix
    }
  }

  return null
}

export const buildLevelCodeMapping = (
  curriculumVersion,
  programType,
  careerPath,
) => {
  const prefix = getCurriculumPrefix(curriculumVersion, programType)

  const hasCareerPath =
    curriculumVersion === '3.0' || curriculumVersion === '3.1'
  const careerSuffix = hasCareerPath ? getCareerPathSuffix(careerPath) : null

  return {
    qualifying: `${prefix}0`,
    level1: `${prefix}1`,
    level2: careerSuffix ? `${prefix}${careerSuffix}2` : `${prefix}2`,
    level3: careerSuffix ? `${prefix}${careerSuffix}3` : `${prefix}3`,
  }
}

const extractQueryData = (queryData) => {
  const { me } = queryData || {}
  if (!me) return null

  const latestProgram = me.educationLatestPgmProgram
  const programRequirements = latestProgram?.programRequirements || []
  const programHistory = me.educationPgmProgramHistory || []
  const prospect = me.prospect

  return {
    latestProgram,
    programRequirements,
    programHistory,
    prospect,
    associateApplication: me.associateApplicationStatus,
    affiliateApplication: me.affiliateApplicationStatus,
    memberApplication: me.memberApplicationStatus,
    finalExamStatus: me.educationFinalExamStatus,
    curriculumVersion: latestProgram?.curriculumVersion || '3.1',
    programType: latestProgram?.programType || 'Associate',
    careerPath: latestProgram?.careerPath,
    currentLevelCode: me.educationCurrentLevel || null,
    mostRecentCompletedLevelCode: me.educationMostRecentCompletedLevel || null
  }
}

const buildLevelState = (requirementData, data, levelCodeMapping) => {
  const levelState = {}

  Object.keys(requirementData).forEach(levelKey => {
    const levelData = requirementData[levelKey]
    const levelCode = levelCodeMapping[levelKey]

    levelState[levelKey] = {
      name: levelData.name,
      requirements: []
    }

    Object.keys(levelData.requirements).forEach(reqKey => {
      const reqData = levelData.requirements[reqKey]

      // Skip requirements that don't apply to this curriculum
      if (shouldExcludeRequirement(reqKey, data.curriculumVersion)) {
        return
      }

      const status = resolveRequirementStatus({
        levelKey,
        reqKey,
        levelCode,
        data
      })

      const programRequirement = data.programRequirements.find(req =>
        req.requirementTypeKey === reqKey && req.levelCode === levelCode
      )

      levelState[levelKey].requirements.push({
        id: reqKey,
        name: reqData.name,
        status: status,
        seminarRegistration: programRequirement?.seminarRegistration
      })
    })
  })

  return levelState
}

export const transformJourneyData = (queryData, requirementData) => {
  // Extract and normalize data
  const data = extractQueryData(queryData)
  if (!data) return null

  // Build level code mapping based on curriculum
  const levelCodeMapping = buildLevelCodeMapping(
    data.curriculumVersion,
    data.programType,
    data.careerPath
  )

  // Build program history data and determine current level
  const completedLevels = getCompletedLevels(data.programHistory)
  const currentLevel = data.currentLevelCode

  // Build the initial level state with statuses
  const levelState = buildLevelState(requirementData, data, levelCodeMapping)

  // Apply default status (ie, 'Locked' until eligible to complete, then 'Ready')
  const levelStateWithStatuses = setDefaultRequirementStatuses(levelState, {
    completedLevels,
    currentLevel,
    LEVEL_KEY_TO_CODE: levelCodeMapping
  })

  // Extract PAT requirement and determine if warning should be shown
  const patRequirement = levelStateWithStatuses.qualifying?.requirements?.find(
    req => req.id === 'playing_ability_test'
  )
  
  const isCurrentlyQualifying = currentLevel?.endsWith('0') || false
  const shouldShowPATWarning = patRequirement && 
                                patRequirement.status !== 'complete' && 
                                !isCurrentlyQualifying

  return {
    ...levelStateWithStatuses,
    currentLevelCode: currentLevel,
    patRequirement: shouldShowPATWarning ? patRequirement : null,
    shouldShowPATWarning
  }
}
